import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import {
  ANY_SPORT_SERIES_SQUAD_CREATED_JOINSECRET,
  ANY_SPORT_SERIES_SQUAD_CREATE_OR_JOIN,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSafePreviousNavigate } from '@sorare/core/src/hooks/useSafePreviousNavigate';

import squadImg from '#src/assets/squad.webp';
import { SquadBackground } from '#src/components/squad/SquadBackground/index.tsx';
import { useCreateSquad } from '#src/hooks/squad/useCreateSquad.ts';

import { SquadContentBlock, SquadFooter } from '../ui';

const Root = styled(Vertical)`
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
`;

const Content = styled(Vertical)`
  padding: var(--triple-unit) 0;
`;

const SquadLogo = styled.img`
  width: 88px;
  height: auto;
  align-self: center;
`;

const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70%;
  background: linear-gradient(to bottom, transparent 0%, var(--c-black) 40%);
  pointer-events: none;
`;

export const SquadCreate = () => {
  const [name, setName] = useState('');
  const { createSquad, loading } = useCreateSquad();
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const safeBack = useSafePreviousNavigate(
    generateSportContextPath(ANY_SPORT_SERIES_SQUAD_CREATE_OR_JOIN)
  );
  const { sport } = useSportContext();
  const intl = useIntl();

  const handleCreate = async () => {
    if (!name.trim()) return;

    const result = await createSquad({ name: name.trim(), sport });
    if (result?.createSquad?.currentUser?.squad) {
      navigate(
        generateSportContextPath(ANY_SPORT_SERIES_SQUAD_CREATED_JOINSECRET, {
          params: {
            joinSecret: result.createSquad.currentUser.squad.joinSecret,
          },
        })
      );
    }
  };

  return (
    <Root>
      <SquadBackground />
      <BottomGradient />
      <SquadContentBlock>
        <Content gap={3}>
          <Vertical gap={1}>
            <BackButton onBack={safeBack} color="quaternary" />
            <BrandM>
              <FormattedMessage defaultMessage="Create a Squad" />
            </BrandM>
            <BodyM color="var(--c-nd-700)">
              <FormattedMessage defaultMessage="Every squad starts with one bold choice!" />
            </BodyM>
          </Vertical>
          <SquadLogo src={squadImg} alt="" />
          <TextInput
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={intl.formatMessage({
              defaultMessage: 'Squad Name',
            })}
          />
        </Content>
      </SquadContentBlock>
      <SquadFooter>
        <Button
          size="large"
          color="primary"
          onClick={handleCreate}
          disabled={!name.trim() || loading}
          fullWidth
        >
          <FormattedMessage defaultMessage="Create squad" />
        </Button>
      </SquadFooter>
      <HideSeriesBottomNav />
    </Root>
  );
};
